import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, AlertTriangle, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const FreeTierCtx = createContext(null);

export const useFreeTier = () => useContext(FreeTierCtx) || {};

export const FreeTierProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedData, setBlockedData] = useState(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'driver') return;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(API + '/api/driver/me/free-tier-status', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setStatus(r.data);
    } catch { /* silent */ }
  }, [isAuthenticated, user]);

  const triggerBlockedModal = useCallback((data) => {
    setBlockedData(data);
    setShowBlockedModal(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onBlock = (e) => triggerBlockedModal(e.detail || {});
    window.addEventListener('free-tier-blocked', onBlock);
    return () => window.removeEventListener('free-tier-blocked', onBlock);
  }, [triggerBlockedModal]);

  const value = { status, refresh, triggerBlockedModal };
  const resetDate = blockedData?.reset_at ? new Date(blockedData.reset_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long' }) : (status?.reset_at ? new Date(status.reset_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long' }) : '');

  return (
    <FreeTierCtx.Provider value={value}>
      {status?.warning && !showBlockedModal && (
        <div className="bg-orange-950/50 border-b border-orange-800 px-4 py-2 flex items-center gap-2 text-sm" data-testid="free-tier-warning-banner">
          <AlertTriangle className="text-orange-400 shrink-0" size={16} />
          <span className="text-orange-200">
            <strong>{status.remaining}</strong> rapport{status.remaining > 1 ? 's' : ''} gratuit{status.remaining > 1 ? 's' : ''} restant{status.remaining > 1 ? 's' : ''} ce mois-ci.
          </span>
          <button onClick={() => navigate('/presentation')} className="ml-auto text-orange-300 underline text-xs" data-testid="free-tier-warning-upgrade">
            Voir les forfaits
          </button>
        </div>
      )}
      {children}
      {showBlockedModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" data-testid="free-tier-blocked-modal">
          <div className="bg-slate-900 rounded-2xl w-full max-w-md border border-amber-600/60 shadow-2xl">
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Crown className="text-amber-400" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Limite gratuite atteinte</h2>
                  <p className="text-xs text-slate-400">Forfait Free : 20 rapports par mois</p>
                </div>
                <button onClick={() => setShowBlockedModal(false)} className="ml-auto text-slate-500 hover:text-white" data-testid="close-blocked-modal">
                  <X size={20} />
                </button>
              </div>
              <p className="text-slate-300 text-sm">
                Vous avez utilise <strong className="text-amber-300">{blockedData?.count || status?.count || 20} / {blockedData?.limit || status?.limit || 20}</strong> rapports gratuits ce mois-ci.
              </p>
              <div className="bg-slate-800 rounded-lg p-3 space-y-1">
                <p className="text-xs text-slate-400 uppercase font-semibold">Passez a un forfait payant pour :</p>
                <ul className="text-sm text-slate-200 space-y-1 mt-2">
                  <li className="flex gap-2"><span className="text-amber-400">+</span> Rapports illimites</li>
                  <li className="flex gap-2"><span className="text-amber-400">+</span> Impression PDF avec logo</li>
                  <li className="flex gap-2"><span className="text-amber-400">+</span> Support prioritaire</li>
                </ul>
              </div>
              {resetDate && (
                <p className="text-xs text-slate-500 text-center">
                  Reinitialisation automatique le <strong>{resetDate}</strong>
                </p>
              )}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  onClick={() => { setShowBlockedModal(false); navigate('/presentation'); }}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  data-testid="btn-view-plans"
                >
                  <Crown className="mr-2" size={18} />
                  Voir les forfaits
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowBlockedModal(false)}
                  className="w-full text-slate-400 hover:text-white"
                  data-testid="btn-continue-free"
                >
                  Continuer gratuitement
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </FreeTierCtx.Provider>
  );
};
